import React, { useEffect, useMemo, useState } from 'react';
import { Button, Collapse, Form, FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import { ForemanArea } from '../../../types/ForemanArea';
import { Activity } from '../../../types/Activity';
import { Project } from '../../../types/Project';
import { Task, TaskStage } from '../../../types/Task';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import FiltersCard from '../../../components/FiltersCard';
import {
  TASK_STATUS_SEQUENCE,
  formatStatusForDisplay,
  sortTaskStatuses,
} from '../../../utils/statusUtils';

/**
 * Fallback status list, used only until /filter-options answers. In task lifecycle order, like
 * the server-supplied list below it - a status picker sorted any other way is unreadable.
 */
const STATUS_OPTIONS = TASK_STATUS_SEQUENCE as TaskStage[];

/**
 * Filter state holds lists for every dropdown, but deep links and older callers can still put a
 * bare (or comma-joined) string in any of them. Everything downstream works on the list form.
 */
const toValueList = (value: string | string[] | undefined | null): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (value === undefined || value === null || value === '') return [];
  return String(value).split(',').map((v) => v.trim()).filter(Boolean);
};

interface TaskFiltersProps {
  tasks: Task[];
  foremanAreas: ForemanArea[];
  /**
   * Only feeds `fallbackActivities`, which is dead while `dynamicOptions.activities` is supplied -
   * and QPTracking always supplies it. Optional so callers need not fetch the whole activity list
   * just to satisfy the prop; QPTracking used to, and threw the result away.
   */
  activities?: Activity[];
  projects: Project[];
  filters: {
    serviceArea: string | string[];
    foremanArea: string[];
    contractYear: string | string[];
    activityNumber: string[];
    projectId: string | string[];
    status: string[];
    costType: string[];
    plannedStartDateFrom: string;
    plannedStartDateTo: string;
    plannedCompletionDateFrom: string;
    plannedCompletionDateTo: string;
  };
  onFilterChange: (name: string, value: string | string[]) => void;
  onClearFilters?: () => void;
  showApplyButton?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
  hasActiveFilters?: boolean;
  /** Filters that differ from their defaults, for the collapsed bar's summary chip (W-iP6). */
  activeFilterCount?: number;
  /** Every applied filter hiding records, defaults included (V2-076, FLT-05). */
  appliedFilterCount?: number;
  /** The page's search, shown in the panel header bar (V2-077, SRCH-01). */
  search?: SearchFieldProps;
  dynamicOptions?: Record<string, any[]>;
  /** Tablet / narrow layout: start collapsed, compact filter card chrome, scrollable body when expanded. */
  isTabletLayout?: boolean;
  defaultCollapsed?: boolean;
}

const TaskFilters: React.FC<TaskFiltersProps> = ({
  tasks,
  foremanAreas,
  activities,
  projects,
  filters,
  onFilterChange,
  onClearFilters,
  showApplyButton = false,
  onApply,
  applyDisabled = false,
  hasActiveFilters = false,
  activeFilterCount,
  appliedFilterCount,
  search,
  dynamicOptions,
  isTabletLayout = false,
  defaultCollapsed = false,
}) => {
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);

  const selectedServiceAreas = useMemo(() => toValueList(filters.serviceArea), [filters.serviceArea]);
  const selectedContractYears = useMemo(() => toValueList(filters.contractYear), [filters.contractYear]);
  const selectedProjects = useMemo(() => toValueList(filters.projectId), [filters.projectId]);

  /**
   * Service Area is a multiselect, so every cascade below matches against a SET rather than one
   * value. An EMPTY set means "no service-area constraint" and must show everything - a bare
   * `selectedServiceAreas.includes(x)` would be false for every option and hide the whole list.
   * A non-empty set is a union: an option is kept when it belongs to ANY selected area, which is
   * the same OR the server applies.
   */
  const selectedServiceAreaSet = useMemo(
    () => new Set(selectedServiceAreas),
    [selectedServiceAreas]
  );
  const matchesSelectedServiceArea = (serviceAreaName: string | undefined): boolean =>
    selectedServiceAreaSet.size === 0 ||
    (serviceAreaName !== undefined && selectedServiceAreaSet.has(serviceAreaName));

  // Only use local filtering as fallback when dynamicOptions is not available
  // This ensures compatibility but prioritizes server-side cross-filtering

  // Fallback service areas (only used if dynamicOptions not available)
  const fallbackServiceAreas = useMemo(() => {
    if (dynamicOptions?.serviceArea) return [];
    
    const uniqueServiceAreas = new Set(
      tasks
        .map(task => task.foremanArea?.serviceArea?.name)
        .filter((name): name is string => name !== undefined)
    );
    
    return Array.from(uniqueServiceAreas)
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ name }));
  }, [tasks, dynamicOptions?.serviceArea]);

  // Fallback foreman areas (only used if dynamicOptions not available)
  const fallbackForemanAreas = useMemo(() => {
    if (dynamicOptions?.foremanArea) return [];
    
    let filtered = tasks.map(task => task.foremanArea).filter((area): area is ForemanArea => area !== undefined);

    filtered = filtered.filter(area => matchesSelectedServiceArea(area.serviceArea?.name));

    // Remove duplicates and sort
    return filtered
      .filter((area, index, self) =>
        index === self.findIndex(a => a.name === area.name)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks, selectedServiceAreaSet, dynamicOptions?.foremanArea]);

  // Fallback activities (only used if dynamicOptions not available)
  const fallbackActivities = useMemo(() => {
    if (dynamicOptions?.activities) return [];
    
    let filtered = activities ?? [];

    filtered = filtered.filter(activity => matchesSelectedServiceArea(activity.serviceArea?.name));

    if (filters.foremanArea.length > 0) {
      const selectedForemanAreas = foremanAreas.filter(
        area => filters.foremanArea.includes(area.name)
      );
      const serviceAreaNames = selectedForemanAreas
        .map(area => area.serviceArea?.name)
        .filter((name): name is string => name !== undefined);
      
      if (serviceAreaNames.length > 0) {
        filtered = filtered.filter(activity => {
          const serviceAreaName = activity.serviceArea?.name;
          return serviceAreaName !== undefined && serviceAreaNames.includes(serviceAreaName);
        });
      }
    }

    // Remove duplicates by activity name and sort
    return filtered
      .filter((activity, index, self) =>
        index === self.findIndex(a => a.activityName === activity.activityName)
      )
      .sort((a, b) => {
        // Sort by activity number first, then by activity name
        const numberA = parseInt(a.activityNumber || '0');
        const numberB = parseInt(b.activityNumber || '0');
        if (numberA !== numberB) {
          return numberA - numberB;
        }
        return a.activityName.localeCompare(b.activityName);
      });
  }, [activities, selectedServiceAreaSet, filters.foremanArea, foremanAreas, dynamicOptions?.activities]);

  // Fallback projects (only used if dynamicOptions not available)
  const fallbackProjects = useMemo(() => {
    if (dynamicOptions?.projects) return [];

    let filtered = projects;

    filtered = filtered.filter(project => matchesSelectedServiceArea(project.serviceArea?.name));

    if (filters.foremanArea.length > 0) {
      filtered = filtered.filter(project => {
        const foremanArea = foremanAreas.find(area => area.id === project.foremanAreaId);
        return foremanArea && filters.foremanArea.includes(foremanArea.name);
      });
    }
    
    // Sort projects by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, selectedServiceAreaSet, filters.foremanArea, foremanAreas, dynamicOptions?.projects]);

  const foremanAreaOptions = dynamicOptions?.foremanArea || fallbackForemanAreas;
  const activityOptions = dynamicOptions?.activities || fallbackActivities;
  const projectOptions = dynamicOptions?.projects || fallbackProjects;

  // The API returns statuses in whatever order the query produced; present them in lifecycle order
  // (Proposed -> ... -> Finalized, Cancelled last) rather than alphabetically.
  const statusOptions = useMemo(
    () => sortTaskStatuses<TaskStage>(dynamicOptions?.status ?? STATUS_OPTIONS, (s) => String(s)),
    [dynamicOptions?.status]
  );

  /**
   * Narrowing the service area can strand child selections that are no longer offered, which would
   * apply a filter the user can no longer see. Prune those - but PRUNE, never wipe: only the
   * entries that dropped out are removed, the rest stay selected. Each effect also waits for its
   * option list to be populated, because pruning against an empty (still-loading) list would wipe
   * selections restored from the URL on first mount.
   */
  useEffect(() => {
    if (filters.foremanArea.length === 0 || foremanAreaOptions.length === 0) return;
    const valid = new Set(foremanAreaOptions.map((area: any) => area.name));
    const updated = filters.foremanArea.filter((name) => valid.has(name));
    if (updated.length !== filters.foremanArea.length) {
      onFilterChange('foremanArea', updated);
    }
  }, [foremanAreaOptions, filters.foremanArea, onFilterChange]);

  useEffect(() => {
    if (filters.activityNumber.length === 0 || activityOptions.length === 0) return;
    const valid = new Set(activityOptions.map((a: any) => String(a.activityNumber)));
    const updated = filters.activityNumber.filter((number) => valid.has(String(number)));
    if (updated.length !== filters.activityNumber.length) {
      onFilterChange('activityNumber', updated);
    }
  }, [activityOptions, filters.activityNumber, onFilterChange]);

  useEffect(() => {
    if (selectedProjects.length === 0 || projectOptions.length === 0) return;
    const valid = new Set(projectOptions.map((p: any) => String(p.id)));
    const updated = selectedProjects.filter((id) => valid.has(String(id)));
    if (updated.length !== selectedProjects.length) {
      onFilterChange('projectId', updated);
    }
  }, [projectOptions, selectedProjects, onFilterChange]);

  // Handle clear filters
  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  return (
    <FiltersCard
      title="Task Filters"
      subtitle="Filter tasks by service area, foreman area, contract year, activity, project, status, and cost type"
      showSubtitle={!isTabletLayout}
      className={['qp-filters-card', isTabletLayout ? 'qp-filters-card--tablet' : ''].filter(Boolean).join(' ')}
      // No scrollable body on tablet any more: a filter panel with its own scroll area was one of
      // the nested scroll traps (W-H5). It starts collapsed there instead (W-iP6).
      collapsible={true}
      defaultCollapsed={defaultCollapsed}
      showClearButton={true}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters}
      activeFilterCount={activeFilterCount}
      appliedFilterCount={appliedFilterCount}
      search={search}
      showApplyButton={showApplyButton}
      onApply={onApply}
      applyDisabled={applyDisabled}
    >
      {/* Row/Col pinned each filter to a fixed 1/12-2/12 fraction regardless of viewport, which is
          why seven filters truncated on a tablet instead of reflowing - FilterGrid auto-fits
          tracks at a minimum readable width instead. */}
      <FilterGrid>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={dynamicOptions?.serviceArea || fallbackServiceAreas}
            value={selectedServiceAreas}
            onChange={(value) => onFilterChange('serviceArea', value.map(v => String(v)))}
            getValue={(area) => area.name}
            formatOption={(area) => area.name}
            formatDisplay={(area) => area.name}
            placeholder="All Service Areas"
            className="tw-w-full"
            label="Service Area"
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={foremanAreaOptions}
            value={filters.foremanArea}
            onChange={(value) => onFilterChange('foremanArea', value.map(v => String(v)))}
            getValue={(area) => area.name}
            formatOption={(area) => area.name}
            formatDisplay={(area) => area.name}
            placeholder="All Foreman Areas"
            className="tw-w-full"
            label="Foreman Area"
            noOptionsMessage={selectedServiceAreas.length > 0 ? "No foreman areas for selected service areas" : "No foreman areas available"}
          />
        </FilterField>
        <FilterField>
          <StandoutDropdown
            type="multiselect"
            options={dynamicOptions?.contractYear || []}
            value={selectedContractYears}
            onChange={(value) => onFilterChange('contractYear', value.map(v => String(v)))}
            getValue={(year) => year}
            formatOption={(year) => year}
            formatDisplay={(year) => year}
            placeholder="All Contract Years"
            className="tw-w-full"
            label="Contract Year"
            noOptionsMessage="No contract years available"
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={activityOptions}
            value={filters.activityNumber}
            onChange={(value) => onFilterChange('activityNumber', value.map(v => String(v)))}
            getValue={(activity) => activity.activityNumber}
            formatOption={(activity) => `#${activity.activityNumber} - ${activity.activityName}`}
            placeholder="All Activities"
            className="tw-w-full"
            label="Activity"
            noOptionsMessage={selectedServiceAreas.length > 0 || filters.foremanArea.length > 0 || selectedContractYears.length > 0 ? "No activities for selected area" : "No activities available"}
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={projectOptions}
            value={selectedProjects}
            onChange={(value) => onFilterChange('projectId', value.map(v => String(v)))}
            getValue={(project) => project.id?.toString?.() ?? project.toString?.() ?? ''}
            formatOption={(project) => {
              if (project.projectNumber && project.name) {
                return `${project.projectNumber} - ${project.name}`;
              }
              return project.name ?? project.toString?.() ?? '';
            }}
            formatDisplay={(project) => project.projectNumber ?? project.name ?? ''}
            placeholder="All Projects"
            className="tw-w-full"
            label="Project"
            noOptionsMessage={selectedServiceAreas.length > 0 || filters.foremanArea.length > 0 || selectedContractYears.length > 0 ? "No projects for selected area" : "No projects available"}
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown<TaskStage>
            options={statusOptions}
            value={filters.status}
            onChange={(value) => onFilterChange('status', value.map(v => String(v)))}
            getValue={(status) => status}
            formatOption={(status) => formatStatusForDisplay(status)}
            // Nothing selected still leaves out Cancelled on the server - say so (V2-076).
            placeholder="All Except Cancelled"
            className="tw-w-full"
            label="Status"
            // statusOptions is already in lifecycle order; without this the dropdown re-sorts it
            // alphabetically by display label and silently discards sortTaskStatuses.
            preserveOptionsOrder
            // Eight fixed stages - a search box over them is noise.
            showSearch={false}
          />
        </FilterField>
        <FilterField>
          <MultiSelectDropdown
            options={dynamicOptions?.costTypes || []}
            value={filters.costType}
            onChange={(value) => onFilterChange('costType', value.map(v => String(v)))}
            getValue={(costType) => costType}
            formatOption={(costType) => costType}
            placeholder="All Cost Types"
            className="tw-w-full"
            label="Cost Type"
            noOptionsMessage="No cost types available"
            // Unit Rate / Fixed Cost / Cost Plus - a fixed short enumeration, same as Status.
            showSearch={false}
          />
        </FilterField>
      </FilterGrid>

      {/* Additional Filters - Collapsible */}
      <div className="tw-mt-3">
        <Button
          variant="link"
          size="sm"
          onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
          className="tw-p-0 tw-no-underline"
          style={{ fontSize: '0.875rem' }}
        >
          <i className={`bi bi-chevron-${showAdditionalFilters ? 'up' : 'down'} tw-mr-1`}></i>
          Additional Filters
        </Button>
        <Collapse in={showAdditionalFilters}>
          <div>
            <FilterGrid className="tw-mt-2">
              <FilterField>
                <Form.Label className="tw-text-sm">Planned Start Date From</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.plannedStartDateFrom || ''}
                  onChange={(e) => onFilterChange('plannedStartDateFrom', e.target.value)}
                  placeholder="From Date"
                />
              </FilterField>
              <FilterField>
                <Form.Label className="tw-text-sm">Planned Start Date To</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.plannedStartDateTo || ''}
                  onChange={(e) => onFilterChange('plannedStartDateTo', e.target.value)}
                  placeholder="To Date"
                />
              </FilterField>
              <FilterField>
                <Form.Label className="tw-text-sm">Planned End Date From</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.plannedCompletionDateFrom || ''}
                  onChange={(e) => onFilterChange('plannedCompletionDateFrom', e.target.value)}
                  placeholder="From Date"
                />
              </FilterField>
              <FilterField>
                <Form.Label className="tw-text-sm">Planned End Date To</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.plannedCompletionDateTo || ''}
                  onChange={(e) => onFilterChange('plannedCompletionDateTo', e.target.value)}
                  placeholder="To Date"
                />
              </FilterField>
            </FilterGrid>
          </div>
        </Collapse>
      </div>
    </FiltersCard>
  );
};

export default TaskFilters;